import React, { useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Search, MoreVertical, Copy, Loader, Loader2 } from "lucide-react";
import Dropdown from "../../../components/ui/Dropdown";
import Pagination from "../../../components/ui/Pagination";
import CustomSelect from "../../../components/ui/CustomSelect";
import { toast } from "react-hot-toast";

import {
  usePendingGiftCardPurchases,
  useRetryGiftCardPurchase,
} from "../hooks/useGiftCardOrders";

const statusOptions = [
  { label: "All Status", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Success", value: "success" },
  { label: "Failed", value: "failed" },
  { label: "Manual Review", value: "manual_review" },
];

const statusStyles = {
  success: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  pending: "bg-yellow-100 text-yellow-800",
  manual_review: "bg-orange-100 text-orange-800",
};

export function GiftCardBuyOrdersTable() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loadingRowId, setLoadingRowId] = useState(null);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError, error } = usePendingGiftCardPurchases({
    page,
    limit,
    status: statusFilter || undefined,
    search: debouncedSearch || undefined,
  });
  const { mutate: retryPurchase } = useRetryGiftCardPurchase();

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const truncateText = (text, length = 10) => {
    if (!text) return "";
    return text.length > length ? `${text.slice(0, length)}...` : text;
  };

  const handleRetry = (order) => {
    if (!order?.uuid) return;
    setLoadingRowId(order.uuid);
    retryPurchase(order.uuid, {
      onSuccess: () => {
        toast.success("Gift card purchase retried successfully");
        setLoadingRowId(null);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to retry purchase");
        setLoadingRowId(null);
      },
    });
  };

  const columns = [
    {
      id: "uuid",
      accessorKey: "uuid",
      header: "Order ID",
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <div className="flex gap-1 justify-between items-center min-w-[130px]">
            <span title={value}>{truncateText(value, 12)}</span>
            <button onClick={() => handleCopy(value)} className="text-gray-500 hover:text-black">
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      id: "customer",
      header: "Customer",
      accessorFn: (row) =>
        row.user ? `${row.user.firstName || ""} ${row.user.lastName || ""}`.trim() : "N/A",
      cell: (info) => (
        <div>
          <p className="font-medium text-gray-800">{info.getValue()}</p>
          <p className="text-xs text-gray-500">{info.row.original.user?.email}</p>
        </div>
      ),
    },
    {
      accessorKey: "productName",
      header: "Product",
      cell: (info) => info.getValue() || "N/A",
    },
    {
      accessorKey: "quantity",
      header: "Qty",
    },
    {
      id: "totalAmount",
      header: "Total Amount",
      accessorFn: (row) => `${row.currencyCode || ""} ${Number(row.totalAmount || 0).toLocaleString()}`,
    },
    {
      accessorKey: "recipientEmail",
      header: "Recipient Email",
      cell: (info) => info.getValue() || "N/A",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue();
        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap uppercase ${statusStyles[status?.toLowerCase()] || "bg-gray-100 text-gray-800"}`}
          >
            {status || "UNKNOWN"}
          </span>
        );
      },
    },
    {
      id: "createdAt",
      accessorKey: "createdAt",
      header: "Created At",
      cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleString() : "N/A"),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const order = row.original;
        const isRowLoading = loadingRowId === order?.uuid;
        const canRetry = order.status?.toLowerCase() === "pending";

        const dropdownItems = [
          ...(canRetry
            ? [
                {
                  label: "Retry Purchase",
                  onClick: () => handleRetry(order),
                  className: "text-emerald-600",
                },
              ]
            : []),
        ];

        if (dropdownItems.length === 0) return null;

        return (
          <Dropdown
            trigger={
              isRowLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <MoreVertical className="w-4 h-4" />
              )
            }
            items={dropdownItems}
            rowIndex={row.index}
          />
        );
      },
    },
  ];

  const records = useMemo(() => data?.data || [], [data]);
  const totalPages = data?.meta?.totalPages || 1;

  const table = useReactTable({
    data: records,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    initialState: {
      pagination: { pageSize: limit, pageIndex: page - 1 },
    },
  });

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    setPage(1);
  };

  if (isError) {
    return (
      <div className="flex flex-1 justify-center items-center h-48">
        <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
          Error: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex gap-4 justify-between items-center pb-6 flex-wrap">
        <div className="relative max-w-xs md:max-w-md">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by reference, email, product..."
            className="py-3 pr-4 pl-12 w-full text-base rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div className="w-52">
          <CustomSelect
            value={statusFilter}
            onChange={handleStatusChange}
            options={statusOptions}
            placeholder="Filter by Status"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-[400px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading orders <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-gray-50/50">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100 hover:border-b">
                {records.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <p className="text-lg font-medium">No gift card orders found</p>
                        <p className="text-sm">Try adjusting your filters or search terms</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 justify-between items-center p-3 border-t border-gray-200 md:p-6 bg-gray-50/50 md:flex-row">
            <div className="text-sm text-gray-600">
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{" "}
              <span className="font-medium">{Math.min(page * limit, data?.meta?.totalItems || 0)}</span>{" "}
              of <span className="font-medium">{data?.meta?.totalItems || 0}</span> entries
            </div>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>
      )}
    </div>
  );
}

export default GiftCardBuyOrdersTable;
